import {
  Box,
  Grid,
  IconButton,
  Button,
  Stack,
  Typography,
  colors,
  lighten,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";
import { useAppStore } from "@/global/store";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import {
  selectIsAnyPerkItemSelected,
  selectPerkItemSelectedMulti,
} from "@/global/store/data/purchaseSeats/cartManager";
import { usePerksManager } from "./usePerksManager";
import { PerkItemCard } from "./PerkItemCard";
import { PerkParentType } from "./perks.types";
import { PerkParentItemIcon } from "./perks.components";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { selectEventsSelected } from "@/global/store/data/purchaseSeats/events";
import { EventType } from "@/global/store/data/types";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { getPerkImageSliderBtnText } from "../utils.perks";

export const PerkParentItem = (props: {
  isOpenByDefault?: boolean;
  perkParent: PerkParentType;
  hideAddAllButton?: boolean;
  sliderImages?: string[];
  onClickImageSliderButton?: () => void;
  hidePerkPrice?: boolean;
}) => {
  const theme = useTheme();
  const { md: mdDown } = useCssMediaQuery("down");
  const { md: mdUp } = useCssMediaQuery("up");
  const [isOpen, setIsOpen] = useState(props?.isOpenByDefault);
  const teamConfig = useTeamConfig();

  // business
  const eventPerkItems = props.perkParent.perks.filter((itm) => itm.gameEventCode !== null); // prettier-ignore
  const isAnyPerkItemSelected = useAppStore(selectIsAnyPerkItemSelected(props.perkParent.perks)); // prettier-ignore
  const isAllPerkItemSelected = useAppStore(selectPerkItemSelectedMulti(eventPerkItems)) // prettier-ignore
  const isAddAllEnabled = props?.perkParent?.groupingType == "multi-select" &&
                          props?.perkParent?.perkType == "event-level" // prettier-ignore
  const hideAddAllButton = props?.hideAddAllButton || false;

  const { onClickAddAll, onClickRemoveAll } = usePerksManager();

  // sorting events by cronologial order
  const eventsSelected = useAppStore(selectEventsSelected);
  // prettier-ignore
  const eventCodeToDate = eventsSelected?.reduce((acc: Record<string, Date>, event: EventType) => {
    acc[event.eventCode] = new Date(event.date);
    return acc;
  }, {});

  // prettier-ignore
  const sortedPerks = props?.perkParent?.perks.sort((a, b) => a?.gameEventCode && b?.gameEventCode
    ? eventCodeToDate[a?.gameEventCode]?.getTime() -
      eventCodeToDate[b?.gameEventCode]?.getTime()
    : 0
  );

  const handleAddAll = () => {
    onClickAddAll(props.perkParent.perks);
    setIsOpen(true);
  };

  const handleRemoveAll = () => {
    onClickRemoveAll(props.perkParent.perks);
    // setIsOpen(false);
  };

  // open accordion if any perk is selected
  React.useEffect(() => {
    if (isAnyPerkItemSelected) setIsOpen(true);
  }, [isAnyPerkItemSelected]);

  return (
    <Accordion
      expanded={isOpen}
      sx={{
        borderRadius: "6 !important",
        boxShadow: "none",
        border: `1px solid ${theme.palette.divider}`,
        paddingTop: 1,
        paddingBottom: 1,
        cursor: "default",
        transition: "all 0.15s ease-in-out",
        "&:hover": {
          boxShadow: `0px 0px 0px 3px ${lighten(
            theme.palette.primary.main,
            0.7
          )}`,
        },
      }}
    >
      <AccordionSummary
        aria-controls="perks-content"
        id={`perk-${props.perkParent.perkCode}`}
        onClick={() => setIsOpen(!isOpen)}
        // sx={{ cursor: "default !important" }}
      >
        <Box width={"100%"}>
          <Stack
            direction={"row"}
            spacing={1.5}
            alignItems={"center"}
            width={"100%"}
          >
            <Stack
              width={"100%"}
              direction={mdDown ? "column" : "row"}
              spacing={mdDown ? 2 : 1.5}
              alignItems={mdDown ? "flex-start" : "center"}
            >
              <Box>
                <PerkParentItemIcon
                  perkParent={props.perkParent}
                  style={{
                    fontSize: mdDown ? "48px" : "32px",
                    color: colors.grey["700"],
                  }}
                />
              </Box>
              <Box>
                <Typography
                  fontWeight={"600"}
                  variant="body1"
                  lineHeight={1.1}
                  color={"text.primary"}
                >
                  {props.perkParent.title}
                </Typography>

                <Typography
                  mt={0.5}
                  variant="body2"
                  color={"text.secondary"}
                  lineHeight={1.1}
                  maxWidth={mdUp ? "350px" : "100%"}
                >
                  {props.perkParent.description}
                </Typography>
              </Box>
              {!mdDown && <Box flexGrow={1} />}

              <Stack direction={"row"} spacing={1}>
                {props?.sliderImages?.length ? (
                  <Button
                    variant="contained"
                    size="large"
                    sx={{
                      fontSize: "0.8rem",
                      lineHeight: "1.1",
                      fontWeight: 600,
                      borderRadius: "32px",
                      paddingLeft: "12px",
                      paddingRight: "12px",
                    }}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      props?.onClickImageSliderButton?.();
                    }}
                  >
                    {getPerkImageSliderBtnText(
                      props.perkParent.perkCode,
                      teamConfig
                    )}
                  </Button>
                ) : null}

                {isAddAllEnabled ? (
                  <>
                    {!hideAddAllButton ? (
                      <ModalActionButton
                        isDisabled={isAllPerkItemSelected}
                        size={"large"}
                        style={{
                          fontSize: "0.8rem",
                          lineHeight: "1.1",
                          fontWeight: 600,
                          borderRadius: "32px",
                          paddingLeft: "12px",
                          paddingRight: "12px",
                        }}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          handleAddAll();
                        }}
                      >
                        Add For All Games
                      </ModalActionButton>
                    ) : null}
                  </>
                ) : null}
              </Stack>
            </Stack>

            <Box>
              <IconButton
                size="medium"
                onClick={() => setIsOpen(!isOpen)}
                sx={{
                  color: colors.grey[800],
                  backgroundColor: `${colors.grey[100]} !important`,
                  transform: `rotate(${isOpen ? 180 : 0}deg)`,
                  transition: `transform 0.15s ease-in-out`,
                  transformOrigin: "center center",
                  "&:active": { backgroundColor: colors.grey[200] },
                }}
              >
                <KeyboardArrowDownRoundedIcon />
              </IconButton>
            </Box>
          </Stack>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {sortedPerks.length > 0 &&
            sortedPerks.map((perkItem) => {
              return (
                <Grid key={perkItem.id} item xs={12}>
                  <PerkItemCard
                    perkParent={props.perkParent}
                    perkItem={perkItem}
                    hideParentIcon={true}
                    hideEventDetails={perkItem.perkType == "order-level"}
                    hideTitleDesc={perkItem.perkType !== "order-level"}
                    hidePrice={props?.hidePerkPrice}
                  />
                </Grid>
              );
            })}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
